import {
  useCloseAllVerifyWorkOrderComplement,
  useCloseAllWorkOrderComplement,
  useCloseVerifyWorkOrderComplement,
  useCloseWorkOrderComplement,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useDisclosure } from '@/hooks';
import { Alert, Button, Form, Modal, notification, Table } from 'antd';
import LGPDTermModal from '../../Complements/ComplementForm/LGPDTermModal';
import { useComplementCloseContext } from '../ComplementCloseModal/ComplementCloseProvider';
import ComplementCloseModal from '../ComplementCloseModal';
import { DateTimeFormat } from '@/utils/moment';
import {
  ComplementCloseFormValues,
  InvalidFieldsTranslation,
  WorkOrderComplementCloseType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { validateRequiredFields } from '@/components/_common/validateRequiredFieldsInObj';
import { WorkOrderComplementFormData } from '../../Complements/ComplementForm';
import { Forms } from '../../WorkOrderFormProvider';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

type Props = {
  open: boolean;
  onClose: () => void;
  forms?: Forms;
};

const ComplementClose: React.FC<Props> = ({
  open: modalOpened,
  onClose: closeModal,
  forms = {},
}) => {
  const [form] = Form.useForm<ComplementCloseFormValues>();

  const history = useHistory();

  const [lgpdModal, { open: openLgpdModal, close: closeLgpdModal }] =
    useDisclosure(false);
  const {
    companyConfig,
    workorder: {
      data: workorder,
      isLoading: workorderIsLoading,
      dataUpdatedAt: workorderDataUpdatedAt,
    },
    complement: { data: complement },
    isAlerted,
    formatFormComplementData,
  } = useComplementCloseContext();

  const prevOpenRef = useRef(false);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = modalOpened;
    if (!wasOpen && modalOpened) {
      if (!complement?.returnDate) {
        form.setFieldValue('hasReturnDate', true);
      }
      form.setFieldValue('closingDate', moment(new Date()));
    } else if (wasOpen && !modalOpened) {
      form.resetFields();
    }
  }, [modalOpened, complement?.returnDate, form]);

  const closeComplement = useCloseWorkOrderComplement();
  const closeVerifyComplement = useCloseVerifyWorkOrderComplement();

  const closeAllComplement = useCloseAllWorkOrderComplement();
  const closeAllVerifyComplement = useCloseAllVerifyWorkOrderComplement();

  const confirmLoadingCloseComplement =
    closeComplement.isLoading || closeVerifyComplement.isLoading;

  const confirmLoadingCloseAllComplement =
    closeAllComplement.isLoading || closeAllVerifyComplement.isLoading;

  const hasReturnDate = Form.useWatch('hasReturnDate', form);

  const notificationError = () => {
    return notification.error({
      message: 'Campos obrigatórios',
      description:
        'Por favor, preencha todos os campos marcados como obrigatórios na aba de Dados Gerais',
    });
  };

  async function handleCloseVerifify(closeType?: WorkOrderComplementCloseType) {
    try {
      if (workorder && complement) {
        let result;

        if (closeType === WorkOrderComplementCloseType.one) {
          result = await closeVerifyComplement.mutateAsync({
            workorderId: workorder.id,
            complementId: complement.id,
          });
        }

        if (closeType === WorkOrderComplementCloseType.all) {
          result = await closeAllVerifyComplement.mutateAsync(workorder.id);
        }

        if (
          result &&
          (result.pendingPurchase?.length ||
            result.pendingDeliveries?.length ||
            result.generateFinancial)
        ) {
          const modalResult = Modal.info({
            icon: null,
            title: 'Não foi possível fechar o orçamento',
            okText: 'Confirmar',
            width: 600,
            content: (
              <>
                {!!result.pendingPurchase?.length && (
                  <>
                    <p style={{ marginBottom: 16 }}>Existe compra pendente:</p>

                    <Table
                      scroll={{ x: true }}
                      pagination={false}
                      dataSource={result.pendingPurchase}
                      columns={[
                        {
                          width: 100,
                          dataIndex: 'description',
                          title: 'Descrição',
                        },
                        {
                          width: 100,
                          dataIndex: 'quantity',
                          title: 'Quantidade',
                        },
                      ]}
                    />
                  </>
                )}

                {!!result.pendingDeliveries?.length && (
                  <>
                    <p style={{ marginBottom: 16 }}>Existe entrega pendente:</p>

                    <Table
                      scroll={{ x: true }}
                      pagination={false}
                      dataSource={result.pendingDeliveries}
                      columns={[
                        {
                          width: 100,
                          dataIndex: 'description',
                          title: 'Descrição',
                        },
                        {
                          width: 100,
                          dataIndex: 'quantity',
                          title: 'Quantidade',
                        },
                      ]}
                    />
                  </>
                )}

                {result.generateFinancial && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 16,
                    }}
                  >
                    <Alert
                      style={{ marginTop: 24 }}
                      type="warning"
                      message="Gerar o lançamento financeiro referente ao orçamento"
                    />

                    <Button
                      onClick={() => {
                        closeModal();
                        modalResult.destroy();
                        history.push(
                          `/app/movements/workOrders/${workorder.id}/complements/${complement.id}/financial`,
                        );
                      }}
                    >
                      Gerar financerio
                    </Button>
                  </div>
                )}
              </>
            ),
          });

          return;
        } else {
          handleCloseComplement(closeType);
        }
      }
    } catch {}
  }

  async function handleCloseComplement(
    closeType?: WorkOrderComplementCloseType,
  ) {
    try {
      if (workorder && complement) {
        const values = await form.validateFields();

        if (closeType === WorkOrderComplementCloseType.one) {
          await closeComplement.mutateAsync({
            workorderId: workorder.id,
            complementId: complement.id,
            setReturnDate: hasReturnDate,
            closingDate: values.closingDate?.format(DateTimeFormat),
          });
        }

        if (closeType === WorkOrderComplementCloseType.all) {
          await closeAllComplement.mutateAsync({
            setReturnDate: hasReturnDate,
            closingDate: values.closingDate?.format(DateTimeFormat),
            workOrderId: workorder.id,
          });
        }

        closeModal();
      }
    } catch {}
  }

  async function validatingFormsGeneralInfo(
    closeType?: WorkOrderComplementCloseType,
  ) {
    try {
      await Promise.all(
        Object.keys(forms).map((key: unknown) =>
          forms[key as keyof Forms]?.validateFields(),
        ),
      );

      handleCloseVerifify(closeType);
    } catch {
      notificationError();
      closeModal();
    }
  }

  async function validatingComplementFormData(
    closeType?: WorkOrderComplementCloseType,
  ) {
    const complementFormData = formatFormComplementData();

    if (complementFormData) {
      const requiredFields: (keyof WorkOrderComplementFormData)[] = [
        'companyId',
        'estimatorId',
        'type',
        'customerType',
        'entryDate',
        ...(companyConfig?.prisma && workorder?.vehicle
          ? (['prisma'] as const)
          : []),
        ...(companyConfig?.isKmRequired && workorder?.vehicle
          ? (['currentKilometers'] as const)
          : []),
        ...(companyConfig?.tankLevel && workorder?.vehicle
          ? (['tankLevel'] as const)
          : []),
      ];

      const invalidFields = validateRequiredFields(
        complementFormData,
        requiredFields,
      );

      if (invalidFields.length > 0) {
        const tableData = invalidFields.map((field) => ({
          key: field,
          fieldName: `Campo '${
            InvalidFieldsTranslation[
              field as keyof typeof InvalidFieldsTranslation
            ]
          }' obrigatório na aba de Dados Gerais`,
        }));

        Modal.info({
          icon: null,
          title: 'Campos obrigatórios',
          okText: 'Confirmar',
          onOk: () => closeModal(),
          content: (
            <Table
              pagination={false}
              dataSource={tableData}
              columns={[
                {
                  title: 'Campo',
                  dataIndex: 'fieldName',
                  key: 'fieldName',
                },
              ]}
            />
          ),
        });
      } else {
        handleCloseVerifify(closeType);
      }
    }
  }

  async function validatingMandatoryFields(
    closeType?: WorkOrderComplementCloseType,
  ) {
    if (forms.generalInfo) {
      validatingFormsGeneralInfo(closeType);
    } else {
      validatingComplementFormData(closeType);
    }
  }

  async function confirmClose(closeType: WorkOrderComplementCloseType) {
    if (isAlerted) {
      openLgpdModal();
    } else {
      validatingMandatoryFields(closeType);
    }
  }

  return (
    <>
      {workorder && (
        <LGPDTermModal
          customerId={workorder.customer?.id}
          open={lgpdModal}
          onClose={closeLgpdModal}
          onCallback={() =>
            handleCloseVerifify(WorkOrderComplementCloseType.one)
          }
          workOrderOverride={workorder}
          isLoadingOverride={workorderIsLoading}
          dataUpdatedAtOverride={workorderDataUpdatedAt}
        />
      )}

      <ComplementCloseModal
        form={form}
        hasReturnDate={!!hasReturnDate}
        open={modalOpened}
        onCancel={closeModal}
        onConfirm={confirmClose}
        key={modalOpened ? 1 : 0}
        confirmLoadingCloseComplement={confirmLoadingCloseComplement}
        confirmLoadingCloseAllComplement={confirmLoadingCloseAllComplement}
      />
    </>
  );
};

export default ComplementClose;
